import { useState, useCallback, useEffect } from 'react';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient, referralService } from '../services/api';
import type { ReferralDashboardDto, ReferralSummaryDto, AdminReferralOverviewDto, AdminReferralItemDto } from '../types/modules';
import { ReferralStatus } from '../types/modules';

const MAX_REFERRALS = 10; // default shown before limit is loaded

const STATUS_LABELS: Partial<Record<ReferralStatus, string>> = {
  [ReferralStatus.Active]:    'Active',
  [ReferralStatus.Pending]:   'Pending',
  [ReferralStatus.Expired]:   'Expired',
  [ReferralStatus.Exhausted]: 'Exhausted',
  [ReferralStatus.Suspended]: 'Suspended',
};

const STATUS_STYLE: Partial<Record<ReferralStatus, React.CSSProperties>> = {
  [ReferralStatus.Active]:    { background: '#dcfce7', color: '#166534' },
  [ReferralStatus.Pending]:   { background: '#fef9c3', color: '#854d0e' },
  [ReferralStatus.Expired]:   { background: '#f3f4f6', color: '#6b7280' },
  [ReferralStatus.Exhausted]: { background: '#fef3c7', color: '#92400e' },
  [ReferralStatus.Suspended]: { background: '#fee2e2', color: '#991b1b' },
};

function StatBox({ icon, label, value, highlight, selected, onClick }: {
  icon: string; label: string; value: string | number;
  highlight?: boolean; selected?: boolean; onClick?: () => void;
}) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      style={{
        ...s.statBox,
        ...(highlight ? { border: '2px solid #10b981', background: '#f0fdf4' } : {}),
        ...(selected  ? { border: '2px solid #047857', background: '#f0fdf4', boxShadow: '0 0 0 3px #bbf7d0' } : {}),
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
        outline: 'none',
        textAlign: 'center' as const,
        width: '100%',
      }}
    >
      <span style={{ fontSize: 22 }}>{icon}</span>
      <span style={{ fontSize: 20, fontWeight: 800, color: highlight || selected ? '#047857' : '#111827' }}>{value}</span>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>
      {onClick && (
        <span style={{ fontSize: 9, color: selected ? '#047857' : '#9ca3af', fontWeight: 700, marginTop: 2 }}>
          {selected ? '▲ Clear' : '▼ Filter'}
        </span>
      )}
    </Wrapper>
  );
}

export default function ReferralsBoardPage() {
  const { getToken, account } = useAppAuth();
  const [data,        setData]        = useState<ReferralDashboardDto | null>(null);
  const [adminData,   setAdminData]   = useState<AdminReferralOverviewDto | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [copied,      setCopied]      = useState(false);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [accepting,   setAccepting]   = useState<string | null>(null);
  const [rejecting,   setRejecting]   = useState<string | null>(null);
  const [error,       setError]       = useState<string | null>(null);
  const [actionMsg,   setActionMsg]   = useState<string | null>(null);
  const [codeInput,   setCodeInput]   = useState('');
  const [codeSubmitting, setCodeSubmitting] = useState(false);
  const [codeMsg,     setCodeMsg]     = useState<{ ok: boolean; text: string } | null>(null);

  const { isAdminOrOps, isRoleLoading } = useRole();
  const [activeFilter, setActiveFilter] = useState<'total' | 'active' | 'earned' | 'month' | null>(null);

  const api = createApiClient(getToken);

  const load = useCallback(async () => {
    if (!account || isRoleLoading) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      if (isAdminOrOps) {
        const overview = await referralService(createApiClient(getToken)).getAllReferrals();
        setAdminData(overview);
      } else {
        const r = await api.get<ReferralDashboardDto>('/api/referrals/dashboard');
        setData(r.data);
      }
    } catch {
      setError('Failed to load referral data.');
    } finally {
      setLoading(false);
    }
  }, [account, getToken, isAdminOrOps, isRoleLoading]);

  useEffect(() => { load(); }, [load]);

  const copyCode = () => {
    if (data?.yourReferralCode) {
      navigator.clipboard.writeText(data.yourReferralCode).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const accept = async (referralId: string, name: string) => {
    setAccepting(referralId);
    setActionMsg(null);
    try {
      await api.post(`/api/referrals/${referralId}/accept`);
      setActionMsg(`✅ Referral for ${name} accepted — they are now eligible.`);
      await load();
    } catch (e: any) {
      setActionMsg(`Failed to accept: ${e?.response?.data?.message ?? 'Please try again.'}`);
    } finally {
      setAccepting(null);
    }
  };

  const reject = async (referralId: string, name: string) => {
    if (!window.confirm(`Reject referral request from ${name}?`)) return;
    setRejecting(referralId);
    setActionMsg(null);
    try {
      await api.post(`/api/referrals/${referralId}/reject`);
      setActionMsg(`Referral for ${name} rejected.`);
      await load();
    } catch (e: any) {
      setActionMsg(`Failed to reject: ${e?.response?.data?.message ?? 'Please try again.'}`);
    } finally {
      setRejecting(null);
    }
  };

  const submitReferralCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setCodeSubmitting(true);
    setCodeMsg(null);
    try {
      const r = await api.post<{ message: string }>('/api/v1/eligibility/referral-code', { referralCode: code });
      setCodeMsg({ ok: true, text: r.data?.message ?? 'Referral code accepted!' });
      setCodeInput('');
    } catch (e: any) {
      setCodeMsg({ ok: false, text: e?.response?.data?.message ?? 'Invalid or unavailable referral code.' });
    } finally {
      setCodeSubmitting(false);
    }
  };

  const withdraw = async (referralId: string, name: string) => {
    if (!window.confirm(`Withdraw earnings from referral of ${name}?`)) return;
    setWithdrawing(referralId);
    try {
      await api.post(`/api/referrals/${referralId}/withdraw`);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Withdrawal failed. Please try again.');
    } finally {
      setWithdrawing(null);
    }
  };

  if (loading || isRoleLoading) return <div style={s.loading}><div style={s.spinner} /></div>;

  if (isAdminOrOps) {
    const items = adminData?.referrals ?? [];
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div>
            <h1 style={s.h1}>All Referrals (Admin)</h1>
            <p style={s.subtitle}>Platform-wide view of all referrals across all users</p>
          </div>
          <button onClick={load} style={s.refreshBtn}>↻ Refresh</button>
        </div>

        {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#991b1b', fontSize: 13 }}>{error}</div>}

        <div style={s.statsGrid}>
          <StatBox icon="👤" label="Total Referrals"  value={adminData?.totalReferrals ?? 0} />
          <StatBox icon="✅" label="Active"            value={adminData?.activeReferrals ?? 0} />
          <StatBox icon="⏳" label="Pending Approval" value={adminData?.pendingReferrals ?? 0} />
          <StatBox icon="💷" label="Total Earned"     value={`£${(adminData?.totalEarned ?? 0).toFixed(2)}`} highlight={true} />
        </div>

        <div style={s.section}>
          <h2 style={s.sectionTitle}>All Referrals ({items.length})</h2>
          {items.length === 0 ? (
            <div style={s.empty}>
              <span style={{ fontSize: 36 }}>👋</span>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '10px 0 4px' }}>No referrals on the platform yet</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={s.tableHead}>
                <span style={{ width: 28 }}>#</span>
                <span style={{ flex: 2 }}>Referrer</span>
                <span style={{ flex: 2 }}>Referee</span>
                <span style={{ width: 90 }}>Status</span>
                <span style={{ width: 72, textAlign: 'center' }}>Jobs</span>
                <span style={{ width: 110, textAlign: 'right' }}>Earned</span>
                <span style={{ width: 108, textAlign: 'right' }}>Expires</span>
              </div>
              {items.map((ref: AdminReferralItemDto, i) => {
                const sc = STATUS_STYLE[ref.status] ?? { background: '#f3f4f6', color: '#6b7280' };
                return (
                  <div key={ref.id} style={s.tableRow}>
                    <span style={{ width: 28, fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>{i + 1}</span>
                    <span style={{ flex: 2, fontWeight: 600, color: '#111827', fontSize: 13 }}>{ref.referrerUserName}</span>
                    <span style={{ flex: 2, color: '#374151', fontSize: 13 }}>{ref.referredUserName}</span>
                    <span style={{ width: 90 }}>
                      <span style={{ ...sc, fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                        {STATUS_LABELS[ref.status] ?? 'Unknown'}
                      </span>
                    </span>
                    <span style={{ width: 72, textAlign: 'center', fontSize: 15, fontWeight: 800, color: '#374151' }}>{ref.totalJobsReferred}</span>
                    <span style={{ width: 110, textAlign: 'right', fontSize: 14, fontWeight: 800, color: '#047857' }}>£{ref.totalEarned.toFixed(2)}</span>
                    <span style={{ width: 108, textAlign: 'right', fontSize: 12, color: '#6b7280' }}>
                      {new Date(ref.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  const allActiveReferrals = (data?.referrals ?? []).filter(r => r.status !== ReferralStatus.Pending);

  const activeReferrals = (() => {
    if (!activeFilter || activeFilter === 'total') return allActiveReferrals;
    if (activeFilter === 'active') return allActiveReferrals.filter(r => r.status === ReferralStatus.Active);
    if (activeFilter === 'earned') return [...allActiveReferrals].filter(r => r.totalEarned > 0).sort((a, b) => b.totalEarned - a.totalEarned);
    if (activeFilter === 'month')  return allActiveReferrals.filter(r => r.status === ReferralStatus.Active);
    return allActiveReferrals;
  })();
  const pendingReferrals  = (data?.referrals ?? []).filter(r => r.status === ReferralStatus.Pending);
  const supplierLimit     = data?.supplierReferralLimit ?? MAX_REFERRALS;
  const supplierActive    = activeReferrals.filter(r => r.status === ReferralStatus.Active).length;
  const slotsUsed         = (data?.referrals ?? []).filter(r =>
    r.status === ReferralStatus.Active || r.status === ReferralStatus.Pending
  ).length;
  const capacityPct = Math.min((slotsUsed / supplierLimit) * 100, 100);
  const barColor    = capacityPct >= 90 ? '#ef4444' : capacityPct >= 70 ? '#f59e0b' : '#10b981';
  const todayEarned = data?.todayEarned ?? 0;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Referrals Board</h1>
          <p style={s.subtitle}>Track your referral network and cumulative earnings</p>
        </div>
        <button onClick={load} style={s.refreshBtn}>↻ Refresh</button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#991b1b', fontSize: 13 }}>
          {error}
        </div>
      )}

      {actionMsg && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#166534', fontSize: 13 }}>
          {actionMsg}
        </div>
      )}

      {/* ── Pending Referral Requests ─────────────────────────────────── */}
      {pendingReferrals.length > 0 && (
        <div style={{ ...s.section, borderLeft: '4px solid #f59e0b', background: '#fffbeb' }}>
          <h2 style={{ ...s.sectionTitle, color: '#92400e', marginBottom: 8 }}>
            ⏳ Pending Referral Requests ({pendingReferrals.length})
          </h2>
          <p style={{ fontSize: 13, color: '#92400e', marginBottom: 14 }}>
            These users entered your referral code and are waiting for your approval.
            Once accepted, they gain eligibility to submit quotes.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fef3c7' }}>
                  {['Name', 'Requested', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#92400e', borderBottom: '1px solid #fde68a' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendingReferrals.map(ref => (
                  <tr key={ref.id} style={{ borderBottom: '1px solid #fef3c7' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#111827' }}>
                      {ref.referredUserName}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>
                      {new Date(ref.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => accept(ref.id, ref.referredUserName)}
                          disabled={accepting === ref.id || rejecting === ref.id}
                          style={{ padding: '5px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: accepting === ref.id ? 0.6 : 1 }}>
                          {accepting === ref.id ? '…' : '✓ Accept'}
                        </button>
                        <button
                          onClick={() => reject(ref.id, ref.referredUserName)}
                          disabled={accepting === ref.id || rejecting === ref.id}
                          style={{ padding: '5px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: rejecting === ref.id ? 0.6 : 1 }}>
                          {rejecting === ref.id ? '…' : '✗ Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Today's referral earnings ───────────────────────────────── */}
      <div style={s.todayBanner}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }}>💰</span>
          <div>
            <p style={{ fontSize: 11, color: '#6d28d9', fontWeight: 600, margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Today's Referral Earnings
            </p>
            <p style={{ fontSize: 34, fontWeight: 900, color: '#1e1b4b', margin: 0, letterSpacing: -1 }}>
              £{todayEarned.toFixed(2)}
            </p>
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#7c3aed', fontStyle: 'italic' }}>
          2% of each job completed by your referees today
        </div>
      </div>

      {/* ── Referral code banner ─────────────────────────────────────── */}
      {data?.yourReferralCode && data.yourReferralCode !== 'N/A' && (
        <div style={s.codeBanner}>
          <div>
            <p style={{ fontSize: 12, color: '#ddd6fe', margin: '0 0 4px', fontWeight: 500 }}>Your Referral Code</p>
            <p style={{ fontSize: 30, fontWeight: 800, color: '#fff', letterSpacing: 3, margin: '0 0 4px', fontFamily: 'monospace' }}>
              {data.yourReferralCode}
            </p>
            <p style={{ fontSize: 11, color: '#c4b5fd', margin: 0 }}>
              Share this code — you earn 2% of every job completed by a referred user.
              Supplier (Carrier/Workshop) referrals require your acceptance.
            </p>
          </div>
          <button onClick={copyCode} style={s.copyBtn}>
            {copied ? '✓ Copied!' : '📋 Copy Code'}
          </button>
        </div>
      )}

      {/* ── Stats — clickable filters ───────────────────────────────── */}
      <div style={s.statsGrid}>
        <StatBox icon="👤" label="Total Referred"   value={data?.totalReferrals ?? 0}
          selected={activeFilter === 'total'}
          onClick={() => setActiveFilter(f => f === 'total' ? null : 'total')} />
        <StatBox icon="✅" label="Active Referrals" value={supplierActive}
          selected={activeFilter === 'active'}
          onClick={() => setActiveFilter(f => f === 'active' ? null : 'active')} />
        <StatBox icon="⏳" label="Pending Approval" value={data?.pendingReferrals ?? 0} />
        <StatBox icon="💷" label="Total Earned"     value={`£${(data?.totalEarned ?? 0).toFixed(2)}`}
          selected={activeFilter === 'earned'}
          onClick={() => setActiveFilter(f => f === 'earned' ? null : 'earned')} />
        <StatBox icon="📅" label="This Month"       value={`£${(data?.monthlyEarned ?? 0).toFixed(2)}`}
          highlight={!activeFilter} selected={activeFilter === 'month'}
          onClick={() => setActiveFilter(f => f === 'month' ? null : 'month')} />
      </div>

      {/* ── Supplier capacity meter ─────────────────────────────────── */}
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
          <div>
            <h2 style={{ ...s.sectionTitle, margin: 0 }}>Supplier Referral Capacity</h2>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
              Carriers and Workshops only — {slotsUsed} of {supplierLimit} slots used (Active + Pending)
            </p>
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: (supplierLimit - slotsUsed) <= 2 ? '#991b1b' : '#374151' }}>
            {Math.max(0, supplierLimit - slotsUsed)} slot{(supplierLimit - slotsUsed) !== 1 ? 's' : ''} remaining
          </span>
        </div>
        <div style={{ height: 10, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${capacityPct}%`, background: barColor, borderRadius: 5, transition: 'width 0.5s ease' }} />
        </div>
        {slotsUsed >= supplierLimit && (
          <p style={{ fontSize: 12, color: '#991b1b', margin: '8px 0 0' }}>
            Supplier referral limit reached. Customer referrals are still unlimited.
            Contact support to request a limit increase.
          </p>
        )}
        <p style={{ fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>
          ℹ Customer referrals have no limit.
        </p>
      </div>

      {/* ── Active/Completed Referrals table ─────────────────────────── */}
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ ...s.sectionTitle, margin: 0 }}>
            {activeFilter
              ? { total: 'All Referrals', active: 'Active Referrals', earned: 'With Earnings', month: 'Active This Month' }[activeFilter]
              : 'Your Referrals'}
            {activeReferrals.length > 0 ? ` (${activeReferrals.length})` : ''}
          </h2>
          {activeFilter && (
            <button
              onClick={() => setActiveFilter(null)}
              style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Show all ×
            </button>
          )}
        </div>

        {activeReferrals.length === 0 ? (
          <div style={s.empty}>
            <span style={{ fontSize: 36 }}>👋</span>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '10px 0 4px' }}>
              {activeFilter ? 'No referrals match this filter' : 'No accepted referrals yet'}
            </p>
            {activeFilter ? (
              <button onClick={() => setActiveFilter(null)} style={{ marginTop: 8, padding: '6px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                Show all referrals →
              </button>
            ) : (
              <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>Share your referral code and accept incoming requests to start earning.</p>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={s.tableHead}>
              <span style={{ width: 28 }}>#</span>
              <span style={{ flex: 2 }}>Referee Name</span>
              <span style={{ width: 90 }}>Status</span>
              <span style={{ width: 72, textAlign: 'center' }}>Jobs</span>
              <span style={{ width: 110, textAlign: 'right' }}>Amount (2%)</span>
              <span style={{ width: 108, textAlign: 'right' }}>Available Date</span>
              <span style={{ width: 110, textAlign: 'right' }}>Action</span>
            </div>

            {activeReferrals.map((ref: ReferralSummaryDto, i) => {
              const sc      = STATUS_STYLE[ref.status] ?? { background: '#f3f4f6', color: '#6b7280' };
              const availDt = new Date(ref.availableDate);
              const isPast  = Date.now() >= availDt.getTime();

              return (
                <div key={ref.id} style={s.tableRow}>
                  <span style={{ width: 28, fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>{i + 1}</span>

                  <span style={{ flex: 2, fontWeight: 600, color: '#111827', fontSize: 14 }}>
                    {ref.referredUserName}
                  </span>

                  <span style={{ width: 90 }}>
                    <span style={{ ...sc, fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                      {STATUS_LABELS[ref.status] ?? 'Unknown'}
                    </span>
                  </span>

                  <span style={{ width: 72, textAlign: 'center', fontSize: 15, fontWeight: 800, color: '#374151' }}>
                    {ref.totalJobsReferred}
                  </span>

                  <span style={{ width: 110, textAlign: 'right', fontSize: 15, fontWeight: 800, color: '#047857' }}>
                    £{ref.totalEarned.toFixed(2)}
                  </span>

                  <span style={{ width: 108, textAlign: 'right', fontSize: 12, color: isPast ? '#047857' : '#6b7280', fontWeight: isPast ? 700 : 400 }}>
                    {availDt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>

                  <span style={{ width: 110, textAlign: 'right' }}>
                    {ref.isWithdrawn ? (
                      <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>Withdrawn</span>
                    ) : ref.canWithdraw ? (
                      <button
                        onClick={() => withdraw(ref.id, ref.referredUserName)}
                        disabled={withdrawing === ref.id}
                        style={withdrawing === ref.id ? s.withdrawBtnDisabled : s.withdrawBtn}
                      >
                        {withdrawing === ref.id ? 'Processing…' : '↑ Withdraw'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>
                        {ref.daysRemaining > 0 ? `${ref.daysRemaining}d left` : '—'}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}

            {/* Totals row */}
            {activeReferrals.length > 1 && (
              <div style={{ ...s.tableRow, borderTop: '2px solid #e5e7eb', marginTop: 4, paddingTop: 12, background: '#f9fafb', borderRadius: 8 }}>
                <span style={{ width: 28 }} />
                <span style={{ flex: 2, fontWeight: 700, color: '#374151', fontSize: 13 }}>Totals</span>
                <span style={{ width: 90 }} />
                <span style={{ width: 72, textAlign: 'center', fontSize: 15, fontWeight: 800, color: '#374151' }}>
                  {activeReferrals.reduce((a, r) => a + r.totalJobsReferred, 0)}
                </span>
                <span style={{ width: 110, textAlign: 'right', fontSize: 15, fontWeight: 800, color: '#047857' }}>
                  £{activeReferrals.reduce((a, r) => a + r.totalEarned, 0).toFixed(2)}
                </span>
                <span style={{ width: 108 }} />
                <span style={{ width: 110 }} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Enter a referral code (referee) ─────────────────────────── */}
      <div style={s.section}>
        <h2 style={s.sectionTitle}>Been referred? Enter a Referral Code</h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>
          If someone shared their referral code with you, enter it here to register the referral.
          The referrer will need to approve it before you gain eligibility.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' as const, alignItems: 'center' }}>
          <input
            value={codeInput}
            onChange={e => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && submitReferralCode()}
            placeholder="Enter referral code"
            maxLength={20}
            style={{ padding: '10px 14px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 15, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2, width: 180, textTransform: 'uppercase' as const }}
          />
          <button
            onClick={submitReferralCode}
            disabled={codeSubmitting || !codeInput.trim()}
            style={{ padding: '10px 20px', background: codeSubmitting || !codeInput.trim() ? '#9ca3af' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: codeSubmitting || !codeInput.trim() ? 'not-allowed' : 'pointer' }}>
            {codeSubmitting ? 'Submitting…' : 'Submit Code'}
          </button>
        </div>
        {codeMsg && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: codeMsg.ok ? '#f0fdf4' : '#fef2f2', color: codeMsg.ok ? '#166534' : '#991b1b', border: `1px solid ${codeMsg.ok ? '#bbf7d0' : '#fca5a5'}` }}>
            {codeMsg.text}
          </div>
        )}
      </div>

      {/* Info note */}
      <div style={s.infoBox}>
        <span>ℹ️</span>
        <p style={{ margin: 0, fontSize: 12, color: '#1d4ed8' }}>
          You earn <strong>2%</strong> of every job completed by a referred user.
          Earnings are locked for <strong>12 months</strong> from the date the referee registered.
          Supplier referrals (Carriers &amp; Workshops) require your approval and are capped at <strong>{supplierLimit}</strong> — customer referrals are unlimited.
        </p>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:              { width: '100%', padding: '24px 16px' },
  loading:           { minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner:           { width: 36, height: 36, border: '3px solid #e5e7eb', borderTop: '3px solid #047857', borderRadius: '50%' },
  header:            { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  h1:                { fontSize: 24, fontWeight: 800, color: '#111827', margin: 0 },
  subtitle:          { fontSize: 13, color: '#6b7280', margin: '4px 0 0' },
  refreshBtn:        { padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13 },
  todayBanner:       { background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)', border: '1.5px solid #c4b5fd', borderRadius: 14, padding: '20px 24px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const },
  codeBanner:        { background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', borderRadius: 16, padding: '24px 28px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' as const },
  copyBtn:           { padding: '10px 20px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' as const },
  statsGrid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 },
  statBox:           { background: '#fff', borderRadius: 10, padding: '16px 12px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1.5px solid transparent' },
  section:           { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
  sectionTitle:      { fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 14px' },
  tableHead:         { display: 'flex', gap: 8, padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: 0.5, alignItems: 'center' },
  tableRow:          { display: 'flex', gap: 8, padding: '12px 10px', alignItems: 'center', borderBottom: '1px solid #f3f4f6' },
  empty:             { textAlign: 'center' as const, padding: '32px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center' },
  infoBox:           { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' },
  withdrawBtn:       { padding: '5px 12px', background: '#047857', border: 'none', borderRadius: 6, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' as const },
  withdrawBtnDisabled: { padding: '5px 12px', background: '#9ca3af', border: 'none', borderRadius: 6, color: '#fff', fontWeight: 700, cursor: 'not-allowed', fontSize: 12, whiteSpace: 'nowrap' as const },
};
